import { type FormEvent, useRef, useState, useEffect } from "react";
import { formatAcademicGrade } from "../../../data/academicGrades";
import { QuestLayout } from "../components/QuestLayout";
import { QuestLoadState } from "../components/QuestPrimitives";
import {
  formatDeadline,
  isAccepting,
  questContactPath,
  questNumberLabel,
  questPath,
} from "../types";
import { isLocalQuestPreview, useQuests } from "../useQuests";

const grades = [
  "高1",
  "高2",
  "高3",
  "高専1",
  "高専2",
  "高専3",
  "高専4",
  "高専5",
  "大1",
  "大2",
  "大3",
  "大4",
  "大5",
  "大6",
  "院1",
  "院2",
  "院3",
  "院4",
  "院5",
  "その他の学生",
];
export function QuestApply({ number }: { number: number }) {
  const data = useQuests(number);
  const quest = data.quests[0];
  const preview = quest?.isDemo || isLocalQuestPreview();
  const [state, setState] = useState<
    "idle" | "sending" | "success" | "error" | "preview"
  >("idle");
  const [message, setMessage] = useState("");
  const [closed, setClosed] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (state === "success" || state === "preview") resultRef.current?.focus();
  }, [state]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!quest || state === "sending") return;
    if (!isAccepting(quest)) {
      setClosed(true);
      return;
    }
    const form = new FormData(event.currentTarget);
    if (preview) {
      setState("preview");
      setMessage(
        "入力確認が完了しました。プレビューのため応募情報は送信・保存されていません。",
      );
      return;
    }
    setState("sending");
    setMessage("");
    try {
      const response = await fetch("/api/job_quest_apply.php", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...Object.fromEntries(form.entries()),
          questNumber: quest.questNumber,
          consent: form.get("consent") === "on",
        }),
      });
      const result = (await response.json()) as {
        status?: string;
        message?: string;
      };
      if (!response.ok || result.status !== "ok")
        throw new Error(result.message || "応募を保存できませんでした。");
      setState("success");
      setMessage(result.message || "エントリーを受け付けました。");
    } catch (error) {
      setState("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "送信できませんでした。通信状態を確認して再度お試しください。",
      );
    }
  }
  return (
    <QuestLayout current="apply">
      <div className="content-width jq-apply-page">
        <nav className="jq-breadcrumb" aria-label="パンくず">
          <a href="/job-quest/board/">Quest Board</a>
          <span aria-hidden="true">/</span>
          <a href={questPath(number)}>Quest Detail</a>
          <span aria-hidden="true">/</span>
          <span>Entry</span>
        </nav>
        <QuestLoadState {...data} />
        {data.status === "ready" && quest && (
          <>
            <header className="jq-page-heading">
              <p className="jq-eyebrow">
                ENTRY / QUEST #{questNumberLabel(quest.questNumber)}
              </p>
              <h1>このQuestに挑戦する</h1>
              <p>
                <strong>{quest.title}</strong>
                <br />
                {quest.company}
              </p>
            </header>
            {state === "success" || state === "preview" ? (
              <div
                ref={resultRef}
                tabIndex={-1}
                className="jq-result"
                role="status"
              >
                <span className="jq-eyebrow">
                  {state === "preview" ? "PREVIEW COMPLETE" : "ENTRY RECEIVED"}
                </span>
                <h2>
                  {state === "preview"
                    ? "入力を確認しました。"
                    : "エントリーを受け付けました。"}
                </h2>
                <p>{message}</p>
                {state === "success" && (
                  <p>
                    選考・契約に関する連絡は、入力いただいたメールアドレスへ行います。
                  </p>
                )}
                <a className="jq-button" href="/job-quest/board/">
                  Quest Boardへ →
                </a>
              </div>
            ) : !isAccepting(quest) || closed ? (
              <div className="jq-empty">
                <p className="jq-eyebrow">QUEST CLOSED</p>
                <h2>このQuestの募集は終了しました。</h2>
                <a className="jq-text-link" href={questPath(number)}>
                  Quest Detailへ戻る →
                </a>
              </div>
            ) : quest.applicationUrl && !quest.isDemo ? (
              <div className="jq-result">
                <h2>企業の応募フォームへ進みます。</h2>
                <p>
                  このQuestは企業が用意したフォームで受け付けています。移動先の個人情報の取り扱いを確認してご応募ください。
                </p>
                <p>応募先：{new URL(quest.applicationUrl).hostname}</p>
                <a
                  className="jq-button"
                  href={quest.applicationUrl}
                  rel="noopener noreferrer"
                >
                  企業の応募フォームを開く ↗
                </a>
                <p>
                  応募後、企業による選考があります。参加はまだ確定しません。
                </p>
              </div>
            ) : (
              <form className="jq-application-form" onSubmit={submit}>
                <p className="jq-form-intro">
                  必要事項を入力してください。応募期間終了後、企業が選考します。
                  <br />
                  応募期限：{formatDeadline(quest.deadline)}
                  {quest.deadline
                    ? "（日本時間）"
                    : "（決まり次第お知らせします）"}
                </p>
                {quest.conditionsNote && (
                  <p className="jq-conditions-note">{quest.conditionsNote}</p>
                )}
                <fieldset disabled={state === "sending"}>
                  <legend className="sr-only">応募者情報</legend>
                  <div className="jq-form-grid">
                    <label>
                      <span>
                        お名前 <em>必須</em>
                      </span>
                      <input
                        name="name"
                        autoComplete="name"
                        required
                        maxLength={100}
                      />
                    </label>
                    <label>
                      <span>
                        メールアドレス <em>必須</em>
                      </span>
                      <input
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        maxLength={254}
                      />
                    </label>
                    <label>
                      <span>
                        現在の学年 <em>必須</em>
                      </span>
                      <select name="grade" defaultValue="" required>
                        <option value="" disabled>
                          選択してください
                        </option>
                        {grades.map((grade) => (
                          <option key={grade} value={grade}>
                            {formatAcademicGrade(grade)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>制作物・ポートフォリオURL（任意）</span>
                      <input
                        name="portfolio"
                        type="url"
                        placeholder="https://"
                        pattern="https://.*"
                        maxLength={1000}
                      />
                      <small>企業が閲覧できるhttps URLをご入力ください。</small>
                    </label>
                    <label className="jq-form-wide">
                      <span>
                        挑戦したい理由・スキル・経験 <em>必須</em>
                      </span>
                      <textarea
                        name="motivation"
                        rows={7}
                        maxLength={4000}
                        required
                        placeholder="Missionに興味を持った理由、関連する経験、取り組める時間など（4,000文字以内）"
                      />
                    </label>
                  </div>
                  <input
                    className="register-honeypot"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div className="jq-consent-info">
                    <h2>応募情報の取り扱い</h2>
                    <p>
                      入力した情報はAIYSSが保存し、
                      {quest.company === "非公開"
                        ? "このQuestの依頼企業（企業名非公開）"
                        : quest.company}
                      に、応募期間終了後、選考のために提供します。AIYSSおよび依頼主は、応募対応・選考・契約に関する連絡に使用します。
                    </p>
                    {quest.company === "非公開" && (
                      <p>
                        依頼企業や応募情報の共有先について確認したい場合は、送信前にAIYSSへお問い合わせください。
                      </p>
                    )}
                    <p>
                      応募内容の訂正・取り下げや個人情報については、
                      <a href={questContactPath(number)}>
                        AIYSSへお問い合わせください
                      </a>
                      。
                    </p>
                    <p>
                      これは業務委託の応募です。応募時点で参加や契約は確定しません。AIYSSは推薦・選考・採用判断を行いません。未成年の方は保護者に相談し、条件を確認してお進みください。
                    </p>
                  </div>
                  <label className="jq-checkbox">
                    <input name="consent" type="checkbox" required />
                    <span>
                      応募条件および上記の情報利用・企業への提供に同意します。
                    </span>
                  </label>
                  <button className="jq-button" type="submit">
                    {state === "sending"
                      ? "送信中…"
                      : preview
                        ? "プレビューの入力内容を確認する"
                        : "この内容でエントリーする"}
                    <span aria-hidden="true">→</span>
                  </button>
                </fieldset>
                {state === "error" && (
                  <p className="jq-form-error" role="alert">
                    {message}
                  </p>
                )}
              </form>
            )}
            <p className="jq-company-link">
              <a href={questPath(number)}>← Quest Detailへ戻る</a>
              <span> / </span>
              <a href={questContactPath(number)}>このQuestについて質問する</a>
            </p>
          </>
        )}
      </div>
    </QuestLayout>
  );
}
